import { noop, nextTick } from "@jetjs/utils";
import { Observable } from "./Observable";
/**
 * recives values as an observer, manipulates the values and act as an observable for the manipulated values.
 *
 *
 * @remarks
 *
 * Example:
 * ``` js
 * function filter(transform(val): boolean) {
 *   let so;
 *   return new ObservableObserver(
 *     (subscriptionObserver) => { so = subscriptionObserver;  // return function to get notified on termination},
 *     (value) => {
 *       let res = transform(value);
 *       if (!res) {
 *         so.next(value); // add to stream if transform returned false
 *       }
 *     }
 *   );
 * }
 *
 * Observable.of("val-1", "val-2")
 *  .pipe(filter(str => str.endsWith("-2")))
 *  .subscribe((str) => console.log("Received: " + str));
 *
 *  // prints:
 *  // Received: val-1
 * ```
 *
 * @param I - type from the values received by this operator (ObservableObserver)
 * @param O - type from the values that will be send back to stream by this operator
 *
 * @public
 */
export class Operator extends Observable {
    constructor(started, next, error, completed) {
        super(subscriptionObserver => {
            return started(subscriptionObserver) || noop;
        });
        this.observables = [];
        this.subscriptions = [];
        this.nextCallback = next;
        this.errorCallback = error;
        this.completedCallback = completed;
    }
    /**
     * Helper to implement a simple Operators.
     *
     * see {@link Operator.pipe}
     * @param fn - function to transform the value. If undefined is returned, value will be removed from the stream.
     *           if an error occur, it will be passed to the #error function
     *
     * @returns Operator instance
     */
    static createSimpleOperator(fn) {
        let so;
        return new Operator((subObserver) => {
            so = subObserver;
            return undefined;
        }, (value) => {
            let result;
            try {
                result = fn(value);
            }
            catch (err) {
                so.error(err);
            }
            if (result) {
                so.next(result);
            }
        });
    }
    subscribe(observerOrNext, error, complete) {
        const subscription = super.subscribe(observerOrNext, error, complete);
        // unsubscribe from parent observables if own subscription was canceled
        subscription.onUnsubscribe(() => {
            this.subscriptions.forEach(s => s.unsubscribe());
        });
        // subscribe to "parent" observables to start receiving values
        this.subscribeUpstream();
        return subscription;
    }
    /**
     * Parent Observable is added, if a Operator is added to a stream via pipe. This is done to implement the laziness.
     * So this Operator can call #subscribe after it has received a subscription.
     *
     * @param observable - The observable to get values from
     * @param cancel - The function to call to cancel the subscription.
     */
    addObservable(observable) {
        this.observables.push(observable);
    }
    start(subscription) {
        this.subscriptions.push(subscription);
    }
    next(value) {
        if (!this.nextObserver) {
            return;
        }
        this.nextCallback(value);
    }
    error(err) {
        if (!this.nextObserver) {
            return;
        }
        if (this.errorCallback) {
            this.errorCallback(err);
        }
        else {
            // if no error callback is provided, route to next observer
            this.nextObserver.error(err);
        }
    }
    complete() {
        if (!this.nextObserver) {
            return;
        }
        if (this.completedCallback) {
            this.completedCallback();
        }
        else {
            // if no complete callback is provided, route to next observer
            this.nextObserver.complete();
        }
    }
    subscribeUpstream() {
        const fn = () => this.observables.forEach(o => o.subscribe(this));
        if (this.nextObserver instanceof Operator) {
            nextTick(fn);
        }
        else {
            fn();
        }
    }
}
//# sourceMappingURL=Operator.js.map